import { useMemo } from "react";

/**
 * Returns whether a task is currently "asleep" — has upstream conditions
 * not yet satisfied AND has never been activated. Rule (product-wide):
 * once activated, a task stays activated. Never re-sleeps automatically.
 *
 * @param {object} task
 * @param {object} taskById - lookup by id (may include done tasks)
 */
export function isTaskSleeping(task, taskById = {}) {
  if (!task) return false;
  if (task.activated_at) return false;
  const ids = task.waiting_for_task_ids || [];
  if (!ids.length) return false;
  for (const uid of ids) {
    const up = taskById[uid];
    // Missing upstream (deleted) is treated as no longer blocking.
    if (up && up.status !== "done") return true;
  }
  return false;
}

/** Hours since ISO string, or Infinity if invalid. */
export function hoursSince(iso) {
  if (!iso) return Infinity;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return Infinity;
  return (Date.now() - t) / 3_600_000;
}

/** Whether the task was activated within the last 24 hours. */
export function isFreshlyActivated(task) {
  return !!task?.activated_at && hoursSince(task.activated_at) < 24;
}

/**
 * Compute the human sentence for a sleeping task's activation.
 * - 1 upstream: "Aktiviraće se kada [Marija] završi [odobrenje kampanje]."
 * - N>1 pending: "Aktiviraće se kada se završe još N stvari."
 * Falls back to unlock_note if no upstream details are available.
 */
export function activationSentence(task, taskById = {}, collabById = {}) {
  if (!task) return "";
  const ids = task.waiting_for_task_ids || [];
  const upstreams = ids.map((id) => taskById[id]).filter(Boolean);
  const pending = upstreams.filter((u) => u.status !== "done");
  if (pending.length === 0) {
    // No upstream data on this page — use the saved note.
    return task.unlock_note || "";
  }
  if (pending.length === 1) {
    const up = pending[0];
    const person = collabById[up.collaborator_id]?.name?.split(" ")[0];
    const title = (up.title || "").trim().replace(/\.$/, "");
    if (person) return `Aktiviraće se kada ${person} završi „${title}".`;
    return `Aktiviraće se kada se završi „${title}".`;
  }
  const rest = pending.length;
  const noun = rest === 2 ? "stvari" : rest === 3 || rest === 4 ? "stvari" : "stvari";
  return `Aktiviraće se kada se završe još ${rest} ${noun}.`;
}

/** React hook: builds a task lookup from a list of tasks. */
export function useTaskById(tasks) {
  return useMemo(() => {
    const map = {};
    for (const t of tasks || []) map[t.id] = t;
    return map;
  }, [tasks]);
}

/** React hook: builds a collaborator lookup. */
export function useCollabById(collabs) {
  return useMemo(() => {
    const map = {};
    for (const c of collabs || []) map[c.id] = c;
    return map;
  }, [collabs]);
}
